import { Ref } from 'vue';

import {
  useCreditReport,
  useOutcomeStore,
  useProfileStore,
  useUserStore
} from '@/stores';

export function useTracking() {
  const handleVisibility = (
    observer: Ref<IntersectionObserver | null>,
    element: Ref<Element | null>,
    callback: () => void,
  ) => {
    if (!window.IntersectionObserver) {
      callback();
      return;
    }

    const onChange = ([entry]: IntersectionObserverEntry[]) => {
      if (entry.isIntersecting) {
        observer.value?.disconnect();
        observer.value = null;
        callback();
      }
    };

    observer.value = new IntersectionObserver(onChange, { threshold: 0.3 });
    if (element.value) {
      observer.value.observe(element.value);
    }
  };

  const trackEecEvent = (eventName: string, data: object) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce_ga4: null });

    window.dataLayer.push({
      event: eventName,
      ecommerce_ga4: {
        items: [data],
      },
    });
  };

  const trackAuthenticationEvent = async () => {
    const { isPositive } = useOutcomeStore();
    const { personalDetails, accountId } = useProfileStore();
    const userStoreState = useUserStore().$state;
    const accountDetails = {
      occupations: {
        id: personalDetails?.employment?.[0].occupationId,
        value: personalDetails?.employment?.[0].occupation,
      },
      businessSectors: {
        id: personalDetails?.employment?.[0].businessSectorId,
        value: personalDetails?.employment?.[0].businessSector,
      },
      topCardsRequestAttempts: userStoreState.topCardsRequestAttempts,
      summaryTopCards: userStoreState.summaryTopCards,
      mortgageResults: userStoreState.mortgageResults,
    };
    const creditReportStore = useCreditReport();
    if (isPositive && !creditReportStore.hasDashboard) {
      await creditReportStore.getCreditProfile();
    }
    const { dashboard } = creditReportStore.fullCreditProfile || {};
    const accountAuthenticationEventData = {
      event: 'authentication_event',
      method: 'onpageload',
      output: 'success',
      authenticated: 'true',
      authService: 'Cognito',
      ...(accountId && { accountId, accountDetails }),
      ...(dashboard?.score && { creditScore: dashboard.score }),
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(accountAuthenticationEventData);
  };

  const pdData = (item_variant: string) => {
    const variants = item_variant.split('|');
    return variants.reduce((result, variant) => {
      const [key, value] = variant.split(':');
      return { ...result, [`pd_${key}`]: value };
    }, {});
  };

  return {
    handleVisibility,
    trackEecEvent,
    trackAuthenticationEvent,
    pdData,
  };
}
